/**
 * Chest storage — Minecraft logic: the player only carries the 9-slot
 * hotbar; extra items live in chests (27 slots each, keyed by block
 * position). Breaking a chest dumps its contents on the ground.
 *
 * MVP note: chest contents are client-side only (not yet synced in
 * multiplayer) — each player sees their own chest contents.
 */

import { createInventory, type Inventory, type InventoryItem } from "@craftgaming/shared";

export const CHEST_SLOTS = 27;

export type ChestStore = {
  /** Inventory for the chest at this position (created on first open). */
  getChest: (wx: number, wy: number, wz: number) => Inventory;
  /** Remove the chest record and return whatever was inside. */
  removeChest: (wx: number, wy: number, wz: number) => InventoryItem[];
};

export function createChestStore(): ChestStore {
  const chests = new Map<string, Inventory>();

  const key = (wx: number, wy: number, wz: number) => `${wx},${wy},${wz}`;

  function getChest(wx: number, wy: number, wz: number): Inventory {
    const k = key(wx, wy, wz);
    let chest = chests.get(k);
    if (!chest) {
      chest = createInventory(CHEST_SLOTS);
      chests.set(k, chest);
    }
    return chest;
  }

  function removeChest(wx: number, wy: number, wz: number): InventoryItem[] {
    const k = key(wx, wy, wz);
    const chest = chests.get(k);
    chests.delete(k);
    if (!chest) return [];
    const contents: InventoryItem[] = [];
    for (const slot of chest.items) {
      if (slot && slot.count > 0) contents.push({ ...slot });
    }
    return contents;
  }

  return { getChest, removeChest };
}
